// Định dạng hiển thị (giờ Việt Nam, tiền VND). Nhận số mili giây, chuỗi ISO hoặc Date.
export const DAY = 86_400_000;
export const MIN = 60_000;
export const TZ = 'Asia/Ho_Chi_Minh';

type When = number | string | Date | null | undefined;
const ms = (t: When) => (t == null || t === '' ? null : typeof t === 'number' ? t : new Date(t).getTime());

const nf = new Intl.NumberFormat('vi-VN');
export const fmtNum = (n: number | string | null | undefined) => nf.format(Math.round(Number(n) || 0));
export const fmtVND = (n: number | string | null | undefined) => `${fmtNum(n)} ₫`;

function trim(x: number, d: number) {
  const t = x.toFixed(d);
  return (t.includes('.') ? t.replace(/\.?0+$/, '') : t).replace('.', ',');
}
/** Tiền rút gọn: 850 tr, 2,35 tỷ. Nhận cả chuỗi bigint từ API. */
export function fmtMoney(n: number | string | null | undefined) {
  const v = Math.round(Number(n) || 0);
  const a = Math.abs(v);
  const s = v < 0 ? '−' : '';
  if (a >= 1e9) return `${s}${trim(a / 1e9, 2)} tỷ`;
  if (a >= 1e6) return `${s}${trim(a / 1e6, a >= 1e8 ? 0 : 1)} tr`;
  if (a >= 1e3) return `${s}${nf.format(a)} ₫`;
  return `${s}${a} ₫`;
}
export const fmtPct = (x: number | null | undefined, d = 0) => `${(x || 0).toFixed(d).replace('.', ',')}%`;
/** Diện tích kiểu Việt Nam: 72.4 ⇒ '72,4', 70 ⇒ '70' (rỗng ⇒ ''). Hiển thị kèm đơn vị: `${fmtArea(a)} m²`. */
export const fmtArea = (area: number | null | undefined) => (area == null ? '' : String(area).replace('.', ','));

const parts = (t: number) => {
  const f = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
  const o: Record<string, string> = {};
  for (const p of f.formatToParts(new Date(t))) o[p.type] = p.value;
  return { y: o.year!, m: o.month!, d: o.day!, h: o.hour === '24' ? '00' : o.hour!, mi: o.minute! };
};
export function fmtDate(t: When) {
  const v = ms(t);
  if (v == null || Number.isNaN(v)) return '—';
  const p = parts(v);
  return `${p.d}/${p.m}/${p.y}`;
}
export function fmtTime(t: When) {
  const v = ms(t);
  if (v == null || Number.isNaN(v)) return '—';
  const p = parts(v);
  return `${p.h}:${p.mi}`;
}
export const fmtDT = (t: When) => (ms(t) == null ? '—' : `${fmtTime(t)} ${fmtDate(t).slice(0, 5)}`);

function fmtIn(s: number) {
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} phút nữa`;
  if (s < 86400) return `${Math.round(s / 3600)} giờ nữa`;
  return `${Math.round(s / 86400)} ngày nữa`;
}
export function fmtAgo(t: When, now = Date.now()) {
  const v = ms(t);
  if (v == null) return '—';
  const s = Math.round((now - v) / 1000);
  if (s < 0) return fmtIn(-s);
  if (s < 60) return 'vừa xong';
  if (s < 3600) return `${Math.floor(s / 60)} phút trước`;
  if (s < 86400) return `${Math.floor(s / 3600)} giờ trước`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)} ngày trước`;
  return fmtDate(v);
}
export function fmtCountdown(left: number) {
  if (left <= 0) return '00:00';
  const pad = (n: number) => String(n).padStart(2, '0');
  const s = Math.floor(left / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h ? `${h}:${pad(m)}:${pad(r)}` : `${pad(m)}:${pad(r)}`;
}
/** Ngày theo giờ Việt Nam dạng YYYY-MM-DD / tháng YYYY-MM. */
export const dateKey = (t: When = Date.now()) => {
  const p = parts(ms(t)!);
  return `${p.y}-${p.m}-${p.d}`;
};
export const monthKey = (t: When = Date.now()) => dateKey(t).slice(0, 7);
export function monthLabel(mk: string) {
  const [y, m] = mk.split('-');
  return `Tháng ${Number(m)}/${y}`;
}

// ---------- Giờ Việt Nam cố định UTC+7 (không DST) cho ô nhập ----------
const VN_OFFSET = 7 * 3_600_000;
/** ISO/epoch ⇒ giá trị cho <input type="datetime-local"> theo giờ Việt Nam ('YYYY-MM-DDTHH:mm'); rỗng ⇒ ''. */
export function vnLocalInput(t: When): string {
  const v = ms(t);
  if (v == null || Number.isNaN(v)) return '';
  return new Date(v + VN_OFFSET).toISOString().slice(0, 16);
}
/** Giá trị <input type="datetime-local"> (hiểu là giờ Việt Nam) ⇒ ISO UTC; sai định dạng ⇒ null. */
export function fromVnLocalInput(s: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(s ?? '').trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, sec] = m.map(Number) as number[];
  const v = Date.UTC(y!, mo! - 1, d!, h!, mi!, sec || 0) - VN_OFFSET;
  return Number.isNaN(v) ? null : new Date(v).toISOString();
}
/** Giờ (0–23) theo giờ Việt Nam — lời chào "Chào buổi sáng/trưa/chiều/tối". */
export function vnHour(t: When = Date.now()) {
  const v = ms(t) ?? Date.now();
  return new Date(v + VN_OFFSET).getUTCHours();
}
/** Số ngày còn lại của tháng (giờ Việt Nam), không tính hôm nay — "còn {n} ngày". */
export function daysLeftInMonth(t: When = Date.now()) {
  const d = new Date((ms(t) ?? Date.now()) + VN_OFFSET);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  return Math.max(0, last - d.getUTCDate());
}

export const initials = (name: string | null | undefined) =>
  String(name || '?')
    .trim()
    .split(/\s+/)
    .slice(-2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
